import axios from 'axios';
import { isCodespaces, getCodespacesStore } from '../utils/subdomain';

// Determina a baseURL correta para o ambiente
const getBaseURL = () => {
    if (isCodespaces()) {
        // No Codespaces, trocar porta 3000 por 3001 na URL pública
        return window.location.origin.replace('-3000.', '-3001.');
    }
    if (process.env.NODE_ENV === 'production') {
        return window.location.origin;
    }
    return process.env.API_LOCAL_URL;
};

// Duas chaves, papéis diferentes:
//
// `aliado_auth`       — os dados do usuário que a UI lê (role, stores, user_id).
//                       Continua sendo o que o App.js e as telas consultam.
// `aliado_auth_token` — a CREDENCIAL: o token assinado pelo servidor no login
//                       (node/utils/authToken.js), enviado em X-Aliado-Auth.
//
// Estavam juntas antes, e era esse o problema: o header era remontado aqui a
// partir de `aliado_auth`, ou seja, o browser afirmava o próprio papel e o
// servidor acreditava. Agora o browser só devolve o que o servidor assinou —
// `aliado_auth` deixa de ser credencial e passa a ser só cache de exibição.
const LS_AUTH = 'aliado_auth';
const LS_AUTH_TOKEN = 'aliado_auth_token';

// Sempre as duas juntas: sobrar uma só produz um estado em que a UI se acha
// logada e toda chamada volta 401 (ou o contrário).
const limparAuthLocal = () => {
    localStorage.removeItem(LS_AUTH);
    localStorage.removeItem(LS_AUTH_TOKEN);
};

/**
 * Sessão expirada: limpa a credencial local e vai para o /login, voltando depois para
 * onde a pessoa estava. Usado pelo interceptor abaixo e pelo iframe do Dash Analítico,
 * cujas requisições não passam por este axios.
 */
const mandarParaLogin = () => {
    // Limpar antes de redirecionar: o localStorage é a fonte da verdade do
    // ProtectedRoute (checkAuth não bate no servidor), então deixá-lo para
    // trás fazia o /login devolver o usuário para uma UI que se acha logada
    // e cujas chamadas todas voltam 401 — laço de redirecionamento.
    // É também o que resolve o primeiro acesso depois deste deploy, com
    // `aliado_auth` antigo e sem token assinado.
    limparAuthLocal();
    // Leva junto onde a pessoa estava. Este é o caminho da sessão que EXPIRA com
    // ela já dentro — sem isto, quem estava lendo um dashboard voltava na home e
    // tinha de reencontrar a tela. O /login valida o destino antes de usar.
    const atual = window.location.pathname + window.location.search;
    window.location.href = (atual && atual !== '/' && !atual.startsWith('/login'))
        ? `/login?redirect=${encodeURIComponent(atual)}`
        : '/login';
};

// Configuração base do axios
const api = axios.create({
    baseURL: getBaseURL(),
    timeout: 180000, // 3 minutos (aumentado de 30s para 180s)
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Interceptor para adicionar auth header
api.interceptors.request.use(
    (config) => {
        // No Codespaces, enviar loja selecionada via header
        if (isCodespaces()) {
            const store = getCodespacesStore();
            if (store) {
                config.headers['X-Aliado-Store'] = store;
            }
        }

        // Header de autenticação: repassa VERBATIM o token que o servidor assinou
        // no login. Nada é montado aqui — remontar era exatamente o furo, porque
        // o payload (papel, empresa) virava afirmação do browser.
        //
        // O `expires` do payload já está dentro do token e é conferido no
        // servidor; a checagem local só evita mandar um token que sabemos morto.
        const token = localStorage.getItem(LS_AUTH_TOKEN);
        if (token) {
            let vivo = true;
            try {
                const authData = JSON.parse(localStorage.getItem(LS_AUTH) || 'null');
                vivo = !authData?.expires || Date.now() < authData.expires;
            } catch (error) {
                vivo = false;
            }
            if (vivo) {
                config.headers['X-Aliado-Auth'] = token;
            } else {
                limparAuthLocal();
            }
        }
        return config;
    },
    (error) => {
        console.error('Erro na requisição:', error);
        return Promise.reject(error);
    }
);

// Interceptor para lidar com erros de autenticação
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            mandarParaLogin();
        }
        return Promise.reject(error);
    }
);

/**
 * Serviço centralizado para todas as chamadas de API
 */
class ApiService {
    // ===== AUTENTICAÇÃO =====

    /**
     * Fazer login
     */
    async login(username, password) {
        try {
            // Usar URL completa para auth, sem o baseURL que tem /api
            const authUrl = isCodespaces()
                ? window.location.origin.replace('-3000.', '-3001.') + '/auth/login'
                : process.env.NODE_ENV === 'production'
                    ? window.location.origin + '/auth/login'
                    : 'http://localhost:3001/auth/login';

            const headers = {
                'Content-Type': 'application/json'
            };

            // No Codespaces, enviar loja selecionada via header
            if (isCodespaces()) {
                const store = getCodespacesStore();
                if (store) {
                    headers['X-Aliado-Store'] = store;
                }
            }

            const response = await axios.post(authUrl, {
                username,
                password,
                subdomain: isCodespaces() ? getCodespacesStore() : undefined
            }, {
                timeout: 30000,
                withCredentials: true,
                headers
            });

            // Se login bem-sucedido, salvar no localStorage
            if (response.data.success) {
                const authData = {
                    authenticated: true,
                    user: response.data.user,
                    subdomain: response.data.subdomain,
                    expires: Date.now() + (24 * 60 * 60 * 1000) // 24 horas
                };
                localStorage.setItem(LS_AUTH, JSON.stringify(authData));
                // A credencial. Sem ela o login "funciona" na tela e toda chamada
                // seguinte volta 401 — um servidor antigo, sem `authToken` na
                // resposta, falha exatamente assim, e é o esperado.
                localStorage.setItem(LS_AUTH_TOKEN, response.data.authToken);
            }

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao fazer login');
        }
    }

    /**
     * Finaliza o login SSO: depois do callback do Google (que já gravou a sessão no
     * servidor), busca o objeto do usuário e grava o localStorage EXATAMENTE como o
     * login por senha grava — mesmas duas chaves, mesmo expires. Se divergir, o
     * X-Aliado-Auth sai sem token e as requisições caem em 401.
     */
    async completeSsoLogin() {
        const response = await api.get('/auth/sso/session');
        if (response.data && response.data.success) {
            const authData = {
                authenticated: true,
                user: response.data.user,
                subdomain: response.data.subdomain,
                expires: Date.now() + (24 * 60 * 60 * 1000) // 24 horas
            };
            localStorage.setItem(LS_AUTH, JSON.stringify(authData));
            localStorage.setItem(LS_AUTH_TOKEN, response.data.authToken);
        }
        return response.data;
    }

    /**
     * Fazer logout
     */
    async logout() {
        try {
            // Limpar localStorage primeiro
            limparAuthLocal();

            // Então fazer logout no servidor
            const response = await api.post('/auth/logout');
            return response.data;
        } catch (error) {
            // Mesmo se der erro no servidor, já limpou o localStorage
            throw new Error(error.response?.data?.error || 'Erro ao fazer logout');
        }
    }

    /**
     * Verificar se usuário está autenticado
     */
    async checkAuth() {
        try {
            // Verificar localStorage
            const localAuth = localStorage.getItem(LS_AUTH);
            // Sem a credencial assinada não há como falar com o servidor, então
            // não adianta a UI se declarar logada. É este ramo que trata o
            // primeiro acesso depois do deploy, com `aliado_auth` da versão
            // anterior: manda para o /login em vez de abrir um painel morto.
            if (!localAuth || !localStorage.getItem(LS_AUTH_TOKEN)) {
                limparAuthLocal();
                return { authenticated: false };
            }

            const authData = JSON.parse(localAuth);

            // Verificar se não expirou (24 horas)
            if (authData.expires && Date.now() > authData.expires) {
                limparAuthLocal();
                return { authenticated: false };
            }

            return {
                authenticated: true,
                user: authData.user,
                subdomain: authData.subdomain
            };

        } catch (error) {
            console.error('Erro na verificação de autenticação:', error);
            limparAuthLocal();
            return { authenticated: false };
        }
    }    /**
     * Obter dados do usuário logado
     */
    async getUserInfo() {
        try {
            // Primeiro tentar localStorage
            const localAuth = localStorage.getItem(LS_AUTH);
            if (localAuth) {
                const authData = JSON.parse(localAuth);
                // `can_sync` ausente = cache gravado por um login ANTERIOR ao campo existir.
                // O cache vale 24h, então sem esta condição quem já estava logado ficaria
                // até um dia sem ver a opção — e o sintoma seria "a feature não subiu".
                // Tratar como obsoleto e buscar do servidor auto-conserta sem exigir
                // logout/login.
                const temCampoNovo = authData.user
                    && Object.prototype.hasOwnProperty.call(authData.user, 'can_sync');
                if (authData.expires && Date.now() < authData.expires && temCampoNovo) {
                    return {
                        success: true,
                        user: authData.user
                    };
                }
            }

            // Fallback para servidor
            const response = await api.get('/auth/me');
            return response.data;
        } catch (error) {
            limparAuthLocal();
            throw new Error(error.response?.data?.error || 'Erro ao obter dados do usuário');
        }
    }

    // ===== GRAVAÇÕES =====

    /**
     * Buscar gravações/atendimentos
     */
    async getRecordings(bucketName) {
        try {
            const response = await api.post('/api/recordings', {
                bucketName
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar gravações');
        }
    }

    /**
     * Iniciar gravação de um microfone
     */
    async startRecording(microphoneData) {
        try {
            const response = await api.post('/api/recording/start', microphoneData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao iniciar gravação');
        }
    }

    /**
     * Parar gravação de um microfone
     */
    async stopRecording(microphoneData) {
        try {
            const response = await api.post('/api/recording/stop', microphoneData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao parar gravação');
        }
    }

    // ===== CONFIGURAÇÕES =====

    /**
     * Obter configuração de lojas
     */
    async getStores() {
        try {
            const response = await api.get('/api/stores');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar lojas');
        }
    }

    // ===== ADMIN =====

    /**
     * Buscar estrutura de empresas/lojas (rápido, sem recorders)
     */
    async getAdminCompanies() {
        try {
            const response = await api.get('/api/admin/companies');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar empresas');
        }
    }

    /**
     * Buscar microfones de UMA empresa (carregamento progressivo)
     */
    async getCompanyMicrophones(bucketName, storeNames) {
        try {
            const response = await api.post('/api/admin/company/microphones', {
                bucketName,
                storeNames
            }, { timeout: 60000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar dados da empresa');
        }
    }

    /**
     * Buscar todos os microfones de todas as empresas (somente admin)
     */
    async getAllMicrophonesAdmin() {
        try {
            const response = await api.get('/api/admin/microphones/all', {
                timeout: 180000 // 3 minutos - sobrescreve o timeout padrão
            });

            return response.data;
        } catch (error) {
            console.error('❌ Erro ao buscar dados administrativos:', error);

            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                throw new Error('Timeout ao buscar dados. A quantidade de dados é muito grande. Tente novamente.');
            }

            throw new Error(error.response?.data?.error || 'Erro ao buscar dados administrativos');
        }
    }

    // ===== UTILITÁRIOS =====

    /**
     * Verificar status da aplicação
     */
    async getHealth() {
        try {
            const response = await api.get('/health');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao verificar status');
        }
    }

    /**
     * Lista lojas disponíveis para um usuário
     * @returns {Promise<Object>} Lista de lojas
     */
    async listStoresByUser() {
        try {
            const response = await api.post('/api/liststoresbyuser', {});
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar lojas:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar lojas',
                stores: []
            };
        }
    }

    /**
     * Cadastra um novo usuário
     * @param {Object} userData - Dados do usuário
     * @returns {Promise<Object>} Resultado do cadastro
     */
    /**
     * Gera o convite de vinculação do Telegram para um usuário.
     * O link volta MONTADO pelo servidor, com o bot do ambiente (são 3 bots: QA, HMG, PROD).
     * Nunca concatenar t.me/<bot> no front: link de produção com token de QA faz a pessoa
     * dar /start no bot errado e receber "convite inválido" sem pista do motivo.
     * @param {number} userId
     * @returns {Promise<Object>} { link, bot_username, expires_at, expires_in_hours, ja_vinculado }
     */
    /**
     * "Já vinculou?" — a tela consulta em loop enquanto o convite está aberto.
     * @param {number} userId
     * @returns {Promise<Object>} { linked, linked_at, telegram_username }
     */
    /**
     * Desvincula o Telegram e desliga a entrega de feedbacks, numa ação só.
     * @param {number} userId
     * @returns {Promise<Object>} { detail, avisado, convites_invalidados }
     */
    async telegramUnlink(userId) {
        try {
            const response = await api.post('/api/telegram/unlink', { user_id: userId });
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || error.response?.data?.error
                    || error.message || 'Erro ao desvincular o Telegram'
            };
        }
    }

    async telegramStatus(userId) {
        try {
            const response = await api.post('/api/telegram/status', { user_id: userId });
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, message: error.response?.data?.detail || error.message };
        }
    }

    async telegramLink(userId) {
        try {
            const response = await api.post('/api/telegram/link', { user_id: userId });
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || error.response?.data?.error
                    || error.message || 'Erro ao gerar convite do Telegram'
            };
        }
    }

    // Página Apps: link do Android e a última versão do desktop do canal deste ambiente.
    async getApps() {
        try {
            const r = await api.get('/api/apps');
            if (!r.data?.desktop) return { success: false, message: 'Resposta fora do formato' };
            return { success: true, data: r.data };
        } catch (error) {
            return { success: false, message: error.response?.data?.error || error.message };
        }
    }

    // URL assinada do instalador desktop (vale ~60 s): pedir só na hora do clique.
    async getLinkInstaladorDesktop(canal) {
        try {
            const r = await api.post('/api/apps/desktop/link', canal ? { canal } : {});
            if (!r.data?.url) return { success: false, message: 'Resposta fora do formato' };
            return { success: true, data: r.data };
        } catch (error) {
            return { success: false, message: error.response?.data?.error || error.message };
        }
    }

    async getWhatsappRelatorios() {
        try {
            const r = await api.get('/api/whatsapp/relatorios');
            return { success: true, data: r.data };
        } catch (error) {
            return { success: false, message: error.response?.data?.error || error.message };
        }
    }

    async getWhatsappMensagens(params) {
        try {
            const r = await api.get('/api/whatsapp/mensagens', { params });
            return { success: true, data: r.data };
        } catch (error) {
            return { success: false, message: error.response?.data?.error || error.message };
        }
    }

    async getWhatsappDestinatarios(scheduleId) {
        try {
            const r = await api.get('/api/whatsapp/destinatarios',
                                    { params: { schedule_id: scheduleId } });
            return { success: true, data: r.data };
        } catch (error) {
            return { success: false, message: error.response?.data?.error || error.message };
        }
    }

    // A recusa de "ficaria sem ninguém" chega como OBJETO, com quem ficou de fora e o que
    // fazer — é o que a tela mostra em vez de um "erro 409". Separar aqui não é preciosismo:
    // jogar esse objeto direto num JSX derruba a tela inteira com "Objects are not valid as
    // a React child", e o erro que aparece deixa de ser o do servidor.
    async _whatsappEscrita(url, body) {
        try {
            const r = await api.post(url, body);
            return { success: true, data: r.data };
        } catch (error) {
            const bruto = error.response?.data?.error;
            const objeto = bruto && typeof bruto === 'object' ? bruto : null;
            return {
                success: false,
                message: objeto ? (objeto.erro || 'Não consegui salvar.')
                                : (bruto || error.message),
                details: objeto
            };
        }
    }

    async salvarWhatsappDestinatarios(body) {
        return this._whatsappEscrita('/api/whatsapp/destinatarios', body);
    }

    async apagarWhatsappAgendamento(body) {
        return this._whatsappEscrita('/api/whatsapp/agendamento/apagar', body);
    }

    async apagarWhatsappRelatorio(body) {
        return this._whatsappEscrita('/api/whatsapp/relatorio/apagar', body);
    }

    async registerUser(userData) {
        try {
            const response = await api.post('/api/user', userData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao cadastrar usuário',
                details: error.response?.data?.details
            };
        }
    }

    /**
     * Lista unidades de uma empresa
     * @param {number} companyId - ID da empresa
     * @returns {Promise<Object>} Lista de unidades
     */
    async getCompanyUnits(companyId) {
        try {
            const response = await api.post('/api/company_units', { company_id: companyId });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar unidades',
            };
        }
    }

    /**
     * Cadastra uma nova unidade
     * @param {Object} unitData - { company_id, name, nickname }
     * @returns {Promise<Object>} Resultado do cadastro
     */
    async createUnit(unitData) {
        try {
            const response = await api.post('/api/unit', unitData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao cadastrar unidade',
            };
        }
    }

    /**
     * Atualiza uma unidade existente
     * @param {Object} unitData - { unit_id, name, nickname }
     * @returns {Promise<Object>} Resultado da atualização
     */
    async updateUnit(unitData) {
        try {
            const response = await api.put('/api/unit', unitData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao atualizar unidade',
            };
        }
    }

    /**
     * Cadastra uma nova loja
     * @param {Object} storeData - { company_id, store_name, store_display_name }
     * @returns {Promise<Object>} Resultado do cadastro
     */
    async createStore(storeData) {
        try {
            const response = await api.post('/api/store', storeData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao cadastrar loja',
                details: error.response?.data?.details
            };
        }
    }

    /**
     * Edita uma loja
     * @param {Object} storeData - { company_id, store_id, store_display_name, app_version_name }
     */
    async updateStore(storeData) {
        try {
            const response = await api.put('/api/store', storeData);
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao editar loja',
                details: error.response?.data?.details
            };
        }
    }

    /**
     * Cadastra uma nova empresa
     * @param {Object} companyData - { companyName, companyBucket, companySubdomain }
     * @returns {Promise<Object>} Resultado do cadastro
     */
    async createCompany(companyData) {
        try {
            const response = await api.post('/api/company', companyData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao cadastrar empresa',
                details: error.response?.data?.details
            };
        }
    }

    /**
     * Edita uma empresa (SOMENTE bucket, default_ia_model e unlock_date)
     * @param {Object} companyData - { company_id, companyBucket?, default_ia_model?, unlock_date? }
     */
    async updateCompany(companyData) {
        try {
            const response = await api.put('/api/company', companyData);
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao editar empresa',
                details: error.response?.data?.details
            };
        }
    }

    /**
     * Lista os modelos de IA habilitados (para os dropdowns)
     * @returns {Promise<Array>} [{ model_key, label, provider }]
     */
    async getIaModels() {
        try {
            const response = await api.get('/api/ia-models');
            return response.data?.models || [];
        } catch (error) {
            console.error('Erro ao buscar modelos de IA:', error);
            return [];
        }
    }

    /**
     * Atualiza um usuário existente
     * @param {Object} userData - { user_id, fullname, username, password?, role }
     * @returns {Promise<Object>} Resultado da atualização
     */
    async updateUser(userData) {
        try {
            const response = await api.put('/api/user', userData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao atualizar usuário',
                details: error.response?.data?.details
            };
        }
    }

    /**
     * Desbloqueia na hora um login bloqueado por excesso de senhas erradas.
     * @param {number} userId - core.users.id
     */
    async unlockUser(userId) {
        try {
            const response = await api.post('/api/user/unlock', { user_id: userId });
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao desbloquear usuário'
            };
        }
    }

    /**
     * Migra um vendedor de loja (mantém o mesmo salesman_id)
     * @param {Object} payload - { company_id, salesman_id, store_id }
     */
    async changeUserStore(payload) {
        try {
            const response = await api.post('/api/user/change-store', payload);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao migrar vendedor de loja'
            };
        }
    }

    /**
     * Listar todos os usuários
     */
    async listUsers() {
        try {
            const response = await api.post('/api/listusers');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar usuários');
        }
    }

    /**
     * Excluir usuário
     * @param {number} userId - ID do usuário a ser excluído
     */
    async deleteUser({ salesman_id, company_id }) {
        try {
            const response = await api.delete('/api/user', {
                data: { salesman_id, company_id }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao excluir usuário');
        }
    }

    /**
     * Buscar feedbacks de um vendedor
     * @param {Object} requestData - Dados da requisição
     * @param {number} requestData.user_id - ID do usuário autenticado
     * @param {string} requestData.salesman - Nome do vendedor
     * @param {string} requestData.start - Data de início (YYYY-MM-DD)
     * @param {string} requestData.end - Data de fim (YYYY-MM-DD)
     * @param {string} requestData.store_name - Nome da loja
     * @returns {Promise<Object>} Lista de feedbacks
     */
    async getFeedbacks(requestData) {
        try {
            const response = await api.post('/api/feedbacks', requestData);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Erro na requisição de feedbacks:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar feedbacks'
            };
        }
    }

    /**
     * Busca feedbacks por empresa/loja/vendedor/período/classificação (admin).
     * requestData: { subdomain, store_id?, salesman_id?, start, end, classification?, limit? }
     */
    async searchFeedbacks(requestData) {
        try {
            const response = await api.post('/api/feedbacks-search', requestData);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro na busca de feedbacks:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.response?.data?.detail || error.message || 'Erro ao buscar feedbacks'
            };
        }
    }

    /**
     * Obter URL pressinada para áudio de comentário
     * @param {number} audioCommentId - ID do comentário de áudio
     * @returns {Promise<Object>} URL pressinada e informações do áudio
     */
    async getAudioCommentUrl(audioCommentId) {
        try {
            const response = await api.post('/api/listen-audio-comment', {
                audio_comment_id: audioCommentId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Erro ao obter URL do áudio:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao obter URL do áudio'
            };
        }
    }

    /**
     * Busca alertas do dia atual
     * @returns {Promise<Object>} Lista de alertas
     */
    async getAlerts() {
        try {
            const response = await api.get('/api/alerts');
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Erro ao buscar alertas:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar alertas'
            };
        }
    }

    async getDislikeAlerts() {
        try {
            const response = await api.get('/api/alerts/dislikes');
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Erro ao buscar alertas de dislike:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar alertas de dislike'
            };
        }
    }

    /**
     * Salvar resposta para um feedback
     * @param {number} feedbackId - ID do feedback
     * @param {string} responseText - Texto da resposta
     * @param {number} [userId] - user_id da linha do feedback
     * @param {number} [storeId] - store_id da linha do feedback
     * @returns {Promise<Object>} Resultado da operação
     */
    async saveFeedbackResponse(feedbackId, responseText, userId, storeId) {
        try {
            const response = await api.post('/api/feedbacks-comments-answer', {
                feedback_id: feedbackId,
                comment_answer: responseText,
                user_id: userId,
                store_id: storeId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Erro ao salvar resposta:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao salvar resposta'
            };
        }
    }

    /**
     * Cria avaliação/comentário do gestor (POST)
     */
    async createManagerComment({ feedback_id, comments, likes, subdomain }) {
        try {
            const response = await api.post('/api/manager-comments', {
                feedback_id,
                comments,
                likes,
                subdomain,
            });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao criar manager_comment:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao salvar avaliação',
            };
        }
    }

    /**
     * Atualiza avaliação/comentário do gestor (PUT)
     */
    async updateManagerComment({ id, feedback_id, comments, likes, subdomain }) {
        try {
            const response = await api.put('/api/manager-comments', {
                id,
                feedback_id,
                comments,
                likes,
                subdomain,
            });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao atualizar manager_comment:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao atualizar avaliação',
            };
        }
    }

    // ===== SUPORTE =====

    /**
     * Obter URL de leitura temporária para um anexo (áudio/imagem) do chat de
     * suporte. A URL devolvida expira rápido (~60s) — buscar na hora de usar,
     * não vale a pena guardar.
     */
    async getReadFileUrl({ fileName, bucketName, fileType }) {
        try {
            const response = await api.post('/api/get-read-file-url', { fileName, bucketName, fileType });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao obter URL do anexo:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao obter URL do anexo'
            };
        }
    }

    /**
     * Sobe um anexo (áudio/imagem) do chat de suporte. Vai pro NOSSO backend
     * (multipart, mesma origem) em vez de direto pro S3 — os buckets de tenant
     * não têm CORS liberado pra PUT vindo do navegador (só o app mobile, que
     * faz upload nativo, usava esse caminho até agora). O Express faz o PUT
     * pro S3 por trás e devolve a `key` real do arquivo (usar como
     * metadata.url da mensagem, não o fileName que foi mandado).
     */
    async uploadChatAttachment({ file, fileName, bucketName, fileType }) {
        try {
            const formData = new FormData();
            formData.append('file', file, fileName);
            formData.append('fileName', fileName);
            formData.append('bucketName', bucketName);
            formData.append('fileType', fileType);
            // Content-Type: undefined derruba o 'application/json' fixo da instância —
            // o browser preenche o multipart/form-data com o boundary certo sozinho.
            const response = await api.post('/api/support-message/upload-attachment', formData, {
                headers: { 'Content-Type': undefined }
            });
            return { success: true, key: response.data?.key };
        } catch (error) {
            console.error('❌ Erro ao subir anexo do chat de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao subir anexo'
            };
        }
    }

    /**
     * Listar mensagens de suporte de uma loja
     */
    async getSupportMessages({ store_id, subdomain, conversation_id }) {
        try {
            const body = { store_id, subdomain };
            if (conversation_id) body.conversation_id = conversation_id;
            const response = await api.post('/api/support-message/list', body);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao buscar mensagens de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar mensagens'
            };
        }
    }

    /**
     * Enviar mensagem de suporte
     */
    async sendSupportMessage({ user_id, user_id_admin, store_id, message, message_type, metadata, conversation_id, subdomain }) {
        try {
            const body = { user_id, store_id, message, message_type: message_type || 'text', subdomain };
            if (user_id_admin) body.user_id_admin = user_id_admin;
            if (metadata !== undefined) body.metadata = metadata;
            if (conversation_id) body.conversation_id = conversation_id;
            const response = await api.post('/api/support-message/send', body);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao enviar mensagem de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao enviar mensagem'
            };
        }
    }

    /**
     * Marca mensagens de suporte como lidas (nunca marca as do próprio user_id).
     */
    async markSupportMessagesRead({ user_id, store_id, ids }) {
        try {
            const response = await api.post('/api/support-message/read', { user_id, store_id, ids });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao marcar mensagens de suporte como lidas:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao marcar mensagens como lidas'
            };
        }
    }

    async deleteSupportMessage({ user_id, store_id, message_id }) {
        try {
            const response = await api.post('/api/support-message/delete', { user_id, store_id, message_id });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao deletar mensagem de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao deletar mensagem'
            };
        }
    }

    /**
     * Buscar transcrição de um feedback
     * @param {number} userId - ID do usuário autenticado
     * @param {string} bucketName - Nome do bucket
     * @param {number} feedbackId - ID do feedback
     * @returns {Promise<Object>} Transcrição do feedback
     */
    // Escopo de conteudo do admin (regra de core.admin_companies).
    async adminScope() {
        try {
            const response = await api.post('/api/admin-scope', {});
            return { success: true, data: response.data };
        } catch (error) {
            // falha aqui nao pode liberar nada: assume o modo MAIS restrito
            return { success: false, data: { restricted: true } };
        }
    }

    async getTranscription(userId, bucketName, feedbackId) {
        try {
            const response = await api.post('/api/transcription', {
                user_id: userId,
                bucket_name: bucketName,
                feedback_id: feedbackId
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Erro ao buscar transcrição:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar transcrição'
            };
        }
    }

    // redacted=true -> audio ANONIMIZADO (trechos sem relacao com a venda silenciados).
    // Admin limitado (core.admin_companies) recebe 403 se pedir o original.
    async listenAudioFeedback(feedbackId, userIdAdmin, bucketName, redacted = false) {
        try {
            const response = await api.post('/api/listen-audio-feedback', {
                feedback_id: feedbackId,
                user_id_admin: userIdAdmin,
                bucket_name: bucketName,
                redacted
            });
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            const status = error.response?.status;
            if (status === 404) {
                return {
                    success: false,
                    notFound: true,
                    message: 'O áudio deste feedback não está mais disponível. O arquivo pode ter sido removido do armazenamento.'
                };
            }
            console.error('❌ Erro ao buscar áudio do feedback:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar áudio'
            };
        }
    }

    // ===== SUPORTE GENÉRICO =====

    /**
     * Lista conversas de suporte (open/closed)
     */
    async getSupportConversations(conversationStatus, userId) {
        try {
            const body = {};
            if (conversationStatus) body.conversation_status = conversationStatus;
            if (userId) body.user_id = userId;
            const response = await api.post('/api/support/conversations', body);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao buscar conversas de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao buscar conversas'
            };
        }
    }

    /**
     * Responde a uma conversa de suporte
     */
    async replySupportConversation({ conversation_id, device_code, support_agent, message }) {
        try {
            const response = await api.post('/api/support/reply', {
                conversation_id,
                device_code,
                support_agent,
                message
            });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao responder conversa de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao enviar resposta'
            };
        }
    }

    /**
     * Encerra uma conversa de suporte
     */
    async closeSupportConversation(conversationId) {
        try {
            const response = await api.post('/api/support/close', {
                conversation_id: conversationId
            });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Erro ao encerrar conversa de suporte:', error);
            return {
                success: false,
                message: error.response?.data?.error || error.message || 'Erro ao encerrar conversa'
            };
        }
    }

    // ===== DASHBOARD =====

    /**
     * Buscar dados do dashboard
     */
    async getDashData({ subdomains, store_ids, salesman_ids, date_start, date_end, fields, no_cache, signal }) {
        try {
            const body = {
                subdomains,
                store_ids: store_ids || [],
                salesman_ids: salesman_ids || [],
                date_start,
                date_end
            };
            // fields limita os nós retornados pelo /dashdata (payload menor/mais rápido).
            if (fields && fields.length) body.fields = fields;
            // no_cache força recomputo e regrava o cache (botão "atualizar dados").
            if (no_cache) body.no_cache = true;
            const response = await api.post('/api/dashdata', body, { signal });
            return response.data;
        } catch (error) {
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') throw error;
            throw new Error(error.response?.data?.error || 'Erro ao buscar dados do dashboard');
        }
    }

    /**
     * Busca empresas do usuário
     */
    async getCompaniesByUser(user_id) {
        try {
            const response = await api.post('/api/companies', { user_id });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar empresas');
        }
    }

    // ===== EMPRESAS PERMITIDAS POR ADMIN =====

    /**
     * Empresas que um admin pode escolher no painel.
     * unrestricted=true significa "todas". Lança com status 403 quando quem pede
     * não é admin irrestrito — a tela usa isso para simplesmente não mostrar o bloco.
     */
    async getAdminCompanyAccess(userId) {
        try {
            const response = await api.get(`/api/admin/company-access/${userId}`);
            return response.data;
        } catch (error) {
            const err = new Error(error.response?.data?.error || 'Erro ao buscar empresas permitidas');
            err.status = error.response?.status;
            throw err;
        }
    }

    /**
     * Define as empresas de um admin. Lista vazia = irrestrito (volta a ver todas).
     */
    async setAdminCompanyAccess(userId, companyIds) {
        try {
            const response = await api.put(`/api/admin/company-access/${userId}`, { company_ids: companyIds });
            return response.data;
        } catch (error) {
            const err = new Error(error.response?.data?.error || 'Erro ao salvar empresas permitidas');
            err.status = error.response?.status;
            throw err;
        }
    }

    /**
     * Envia pergunta ao agente de IA
     */
    async agentQuery({ subdomain, question, user_id, chat_history }) {
        try {
            const response = await api.post('/api/agent/query', {
                subdomain,
                question,
                user_id,
                chat_history: chat_history || []
            }, { timeout: 180000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao consultar agente');
        }
    }

    // ===== PROMPT EDITOR =====

    async promptEditorGetCompanies() {
        try {
            const response = await api.get('/api/prompt-editor/companies');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar empresas');
        }
    }

    async promptEditorGetStores(subdomain) {
        try {
            const response = await api.get(`/api/prompt-editor/stores/${subdomain}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar lojas');
        }
    }

    async promptEditorGetSlugs(subdomain) {
        try {
            const response = await api.get(`/api/prompt-editor/prompt-assignments/${subdomain}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar slugs');
        }
    }

    // `phase_id` opcional: com ele a API resolve pela cascata fase > loja > global (§3.1).
    async promptEditorResolveScoringPrompt({ subdomain, slug, store_id, phase_id }) {
        try {
            const response = await api.post('/api/prompt-editor/resolve-scoring-prompt', { subdomain, slug, store_id: store_id || null, phase_id: phase_id || null });
            // External API wraps in { success, data }; unwrap if needed
            const payload = response.data;
            return (payload && payload.data) ? payload.data : payload;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao resolver prompt');
        }
    }

    async promptEditorResolveActivePrompt({ subdomain, slug, store_id, phase_id }) {
        try {
            const response = await api.post('/api/prompt-editor/resolve-active-prompt', { subdomain, slug, store_id: store_id || null });
            const payload = response.data;
            return (payload && payload.data) ? payload.data : payload;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao resolver prompt ativo');
        }
    }

    async promptEditorGetPromptData(subdomain, promptId) {
        try {
            const response = await api.get(`/api/prompt-editor/prompts/${subdomain}/${promptId}`);
            const payload = response.data;
            return (payload && payload.data) ? payload.data : payload;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar dados do prompt');
        }
    }

    // `phase_id` opcional: com ele a API grava em phase_prompt_assignments (§3.1) em vez de
    // prompt_assignments, e recusa (400) slug exclusivo de outro tipo de fase.
    async promptEditorUpsertPrompt({ subdomain, content, slug, store_id, original_id, ia_model, phase_id }) {
        try {
            const response = await api.post('/api/prompt-editor/upsert-prompt', { subdomain, content, slug, store_id: store_id || null, original_id, ia_model: ia_model || null, phase_id: phase_id || null });
            const payload = response.data;
            return (payload && payload.data) ? payload.data : payload;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao salvar prompt');
        }
    }

    async promptEditorChangeScoringPromptQuestions({ subdomain, slug, store_id, prompt, questions, ia_model, phase_id }) {
        try {
            const body = { subdomain, slug, store_id: store_id || null, questions, phase_id: phase_id || null };
            if (prompt !== undefined && prompt !== null) body.prompt = prompt;
            if (ia_model !== undefined) body.ia_model = ia_model || null;
            const response = await api.post('/api/prompt-editor/change-scoring-prompt-questions', body);
            const payload = response.data;
            return (payload && payload.data) ? payload.data : payload;
        } catch (error) {
            // `error` (do BFF) já traz o detail da API quando existe; `details.detail` cobre o
            // caminho em que o corpo da API sobe inteiro. Sem isso o toast mostrava só
            // "Request failed with status code 400" e a explicação real morria no meio.
            const d = error.response?.data;
            throw new Error(d?.details?.detail || d?.error || d?.detail || 'Erro ao atualizar perguntas');
        }
    }

    /** Prompts configurados numa fase + quais slugs o TIPO dela pode configurar (§3.1). */
    async phasePromptAssignmentsList({ subdomain, phase_id }) {
        const r = await api.post('/api/phases/prompt-assignments/list', { subdomain, phase_id });
        return r.data;
    }

    // Com `phase_id`, remove o override DA FASE (a API exige exatamente um entre store_id e
    // phase_id, e nunca deixa remover o global).
    async promptEditorDeletePromptAssignment({ subdomain, slug, store_id, phase_id }) {
        try {
            const response = await api.post('/api/prompt-editor/delete-prompt-assignment', { subdomain, slug, store_id: phase_id ? null : store_id, phase_id: phase_id || null });
            const payload = response.data;
            return (payload && payload.data) ? payload.data : payload;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao fazer a loja usar o prompt global');
        }
    }

    async adminGetAiCostSummary(days = 30) {
        try {
            const response = await api.get(`/api/admin/ai-cost-summary?days=${days}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar custo de IA');
        }
    }

    // Série temporal do custo (total / LLM / transcrição) para o gráfico de tendência.
    async adminGetAiCostSeries(days = 30, subdomain = null, granularity = null) {
        try {
            const qs = new URLSearchParams({ days: String(days) });
            if (subdomain) qs.set('subdomain', subdomain);
            if (granularity) qs.set('granularity', granularity);
            const response = await api.get(`/api/admin/ai-cost-series?${qs.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar a série de custo');
        }
    }

    // Os últimos N atendimentos, um por um, com a quebra por etapa. Serve para conferir
    // efeito de mudança no pipeline, que a média de 7/30/90 dias esconde.
    async adminGetAiCostRecent(limit = 10, subdomain = null) {
        try {
            const qs = new URLSearchParams({ limit: String(limit) });
            if (subdomain) qs.set('subdomain', subdomain);
            const response = await api.get(`/api/admin/ai-cost-recent?${qs.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar os últimos atendimentos');
        }
    }

    async promptEditorGetCompaniesWithPrompts() {
        try {
            const response = await api.get('/api/prompt-editor/companies-with-prompts');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar empresas com prompts');
        }
    }

    async promptEditorCopyDefaultPrompts({ source_subdomain, target_subdomain }) {
        try {
            const response = await api.post('/api/prompt-editor/copy-default-prompts', { source_subdomain, target_subdomain }, { timeout: 120000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao copiar prompts padrão');
        }
    }

    async promptEditorExportPrompts({ subdomain, store_id }) {
        try {
            const params = new URLSearchParams({ subdomain });
            if (store_id != null && store_id !== '') params.set('store_id', store_id);
            const response = await api.get(`/api/prompt-editor/export-prompts?${params.toString()}`, {
                responseType: 'blob',
                timeout: 60000,
            });
            const cd = response.headers['content-disposition'] || '';
            const filename = cd.match(/filename="?([^";]+)"?/)?.[1] || `prompts_${subdomain}.xlsx`;
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            return { filename };
        } catch (error) {
            let detail;
            if (error.response?.data instanceof Blob) {
                try { detail = JSON.parse(await error.response.data.text())?.error; } catch (_e) { /* binário */ }
            }
            throw new Error(detail || error.response?.data?.error || 'Erro ao exportar prompts');
        }
    }

    async promptEditorCopyStorePrompts({ subdomain, source_store_id, target_store_id }) {
        try {
            const response = await api.post('/api/prompt-editor/copy-store-prompts', { subdomain, source_store_id, target_store_id }, { timeout: 120000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao copiar prompts entre lojas');
        }
    }

    async promptEditorUploadPlaybook({ file, subdomain, referenceSubdomain }) {
        try {
            const params = new URLSearchParams({
                filename: file.name,
                subdomain,
                reference_subdomain: referenceSubdomain || 'leroymerlin',
            });
            const response = await api.post(`/api/prompt-editor/upload-playbook?${params.toString()}`, file, {
                headers: { 'Content-Type': 'application/octet-stream' },
                timeout: 120000,
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao enviar playbook');
        }
    }

    async promptEditorImportPlaybookText({ file, subdomain, storeId }) {
        try {
            const params = new URLSearchParams({
                filename: file.name,
                subdomain,
            });
            if (storeId) params.set('store_id', storeId);
            const response = await api.post(`/api/prompt-editor/import-playbook-text?${params.toString()}`, file, {
                headers: { 'Content-Type': 'application/octet-stream' },
                timeout: 120000,
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao importar texto do playbook');
        }
    }

    async promptEditorGetPlaybookJob(jobId) {
        try {
            const response = await api.get(`/api/prompt-editor/playbook-job/${encodeURIComponent(jobId)}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao consultar processamento do playbook');
        }
    }

    async promptEditorStartTest({ subdomain, data_size, slug, prompt_id, new_dataset, store_id }) {
        try {
            const response = await api.post('/api/prompt-editor/start-test', {
                subdomain, data_size, slug, prompt_id, new_dataset: !!new_dataset, store_id,
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao iniciar teste de prompt');
        }
    }

    async promptEditorGetTestStatus(runId) {
        try {
            const response = await api.get(`/api/prompt-editor/test-status/${encodeURIComponent(runId)}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao consultar status do teste');
        }
    }

    // ===== AWON MONITOR =====

    async awonGetTenants() {
        try {
            const response = await api.get('/api/awon/tenants');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar tenants AWON');
        }
    }

    // periodo: { days } ou { date_start, date_end } (AAAA-MM-DD; a API usa o dia inteiro, 00:00:00 a 23:59:59)
    async awonGetStats(subdomain, periodo) {
        try {
            const response = await api.get('/api/awon/stats', { params: { subdomain, ...periodo } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao buscar estatísticas AWON');
        }
    }

    // "Nova avaliação da IA" de um recorte: espera a decisão; devolve { decision, confidence, reason, before, ... }
    async awonAiRun(subdomain, sessionId) {
        try {
            const response = await api.post('/api/awon/ai-review/run', { subdomain, session_id: sessionId });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao pedir a nova avaliação da IA');
        }
    }

    // "Corrigir análise": propõe o ajuste do prompt + nova classificação; aceitar grava o prompt e reavalia; recusar volta
    async awonCorrectionPropose(subdomain, sessionId, comment, previousId = null) {
        try {
            const response = await api.post('/api/awon/ai-review/correction/propose',
                { subdomain, session_id: sessionId, comment, previous_id: previousId }, { timeout: 240000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao analisar a correção');
        }
    }

    async awonCorrectionAccept(subdomain, correctionId) {
        try {
            const response = await api.post('/api/awon/ai-review/correction/accept',
                { subdomain, correction_id: correctionId }, { timeout: 240000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao aceitar o ajuste');
        }
    }

    async awonCorrectionReject(subdomain, correctionId, comment = null) {
        try {
            const response = await api.post('/api/awon/ai-review/correction/reject',
                { subdomain, correction_id: correctionId, comment });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao recusar o ajuste');
        }
    }

    // nova avaliação em lote: { dry_run: true } só conta; periodo = { days } | { date_start, date_end }
    async awonAiRunMany(subdomain, periodo, dryRun = true) {
        try {
            const response = await api.post('/api/awon/ai-review/run-many', { subdomain, ...periodo, dry_run: dryRun });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao pedir a nova avaliação em lote');
        }
    }

    async awonGetSessions(subdomain, filters = {}) {
        try {
            const response = await api.get('/api/awon/sessions', { params: { subdomain, ...filters } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar atendimentos AWON');
        }
    }

    // Julgador smart (julgador isento): rodadas do período ({date_start, date_end, mode?}), uma rodada, desfazer/freio
    async awonJudgeRounds(subdomain, filtros = {}) {
        try {
            const response = await api.get('/api/awon/judge/rounds', { params: { subdomain, ...filtros } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar as rodadas do julgador');
        }
    }

    // decisões do período ({date_start, date_end, mode?, salesman_id?}), com a hora e o modo da rodada
    async awonJudgeCommands(subdomain, filtros = {}) {
        try {
            const response = await api.get('/api/awon/judge/commands', { params: { subdomain, ...filtros } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar as decisões do julgador');
        }
    }

    // recortes do worker1 que o julgador ainda não processou ({date_start, date_end}) -> {pending_ids, mode, next_round}
    async awonJudgePending(subdomain, filtros = {}) {
        try {
            const response = await api.get('/api/awon/judge/pending', { params: { subdomain, ...filtros } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar os recortes esperando o julgador');
        }
    }

    async awonJudgeConfig(subdomain) {
        try {
            const response = await api.get('/api/awon/judge/config', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao ler o modo do julgador');
        }
    }

    // pessoa decide um recorte indeciso ou reprovado pelo julgador: action = aprovar | descartar
    async awonJudgeHuman(subdomain, sessionId, action) {
        try {
            const response = await api.post('/api/awon/judge/human', { subdomain, session_id: sessionId, action },
                { timeout: 120000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao decidir o recorte');
        }
    }

    async awonJudgeHumanIntervalo(subdomain, commandId, action) {
        try {
            const response = await api.post('/api/awon/judge/human/intervalo', { subdomain, command_id: commandId, action },
                { timeout: 120000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao decidir o indeciso');
        }
    }

    // rodar uma rodada pelo Monitor (só QA): os horários de hoje por loja, e o pedido de um horário
    async awonJudgeForcar(subdomain) {
        try {
            const response = await api.get('/api/awon/judge/forcar', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao carregar as rodadas de hoje');
        }
    }

    async awonJudgeForcarPedir(subdomain, storeId, horario) {
        try {
            const response = await api.post('/api/awon/judge/forcar', { subdomain, store_id: storeId, horario });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao pedir a rodada');
        }
    }

    // base de teste para hoje (só QA): o molde, o que já existe hoje e a cópia em andamento; e o pedido da cópia
    async awonJudgeBaseTeste(subdomain) {
        try {
            const response = await api.get('/api/awon/judge/base-teste', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao carregar a base de teste');
        }
    }

    async awonJudgeBaseTesteTrazer(subdomain) {
        try {
            const response = await api.post('/api/awon/judge/base-teste', { subdomain });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao trazer a base de teste');
        }
    }

    // material de um indeciso: a decisão e, por recorte (ou trecho sem recorte), as falas com o segundo no áudio
    async awonJudgeIndeciso(subdomain, commandId) {
        try {
            const response = await api.get('/api/awon/judge/indeciso', { params: { subdomain, command_id: commandId } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.status === 404 ? 'Indeciso não encontrado'
                : (error.response?.data?.error || 'Erro ao carregar a transcrição do indeciso'));
        }
    }

    async awonJudgeRound(subdomain, roundId) {
        try {
            const response = await api.get('/api/awon/judge/round', { params: { subdomain, round_id: roundId } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao abrir a rodada do julgador');
        }
    }

    // alvo = { command_id } (desfazer) | { round_id } (freio)
    async awonJudgeUndo(subdomain, alvo) {
        try {
            const response = await api.post('/api/awon/judge/undo', { subdomain, ...alvo }, { timeout: 240000 });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao desfazer a decisão do julgador');
        }
    }

    // Juiz de IA dos recortes em revisão
    async awonAiConfig(subdomain) {
        try {
            const response = await api.get('/api/awon/ai-review/config', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao ler o automático do juiz de IA');
        }
    }

    async awonAiSetConfig(subdomain, autoEnabled) {
        try {
            const response = await api.post('/api/awon/ai-review/config', { subdomain, auto_enabled: autoEnabled });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao alterar o automático do juiz de IA');
        }
    }

    async awonAiAction(subdomain, sessionId, action) {
        try {
            const response = await api.post('/api/awon/ai-review/action', { subdomain, session_id: sessionId, action });
            return response.data;
        } catch (error) {
            const d = error.response?.data;
            throw new Error(d?.details?.detail || d?.error || 'Erro na ação do juiz de IA');
        }
    }

    async awonAiClearAll(subdomain, decision) {
        try {
            const response = await api.post('/api/awon/ai-review/clear-all', { subdomain, decision });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao limpar a lista');
        }
    }

    async awonUpdateSession(payload) {
        try {
            const response = await api.put('/api/awon/session', payload);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao atualizar atendimento AWON');
        }
    }

    async awonGetKeyPhrases(subdomain) {
        try {
            const response = await api.get('/api/awon/key-phrases', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar frases AWON');
        }
    }

    // áudio de um recorte ({session_id}), de uma voz ({embedding_id}) ou do trecho de um indeciso ({command_id, item})
    // como Blob — fica só na memória da aba
    async awonGetAudio(subdomain, params) {
        try {
            const response = await api.get('/api/awon/audio', { params: { subdomain, ...params }, responseType: 'blob' });
            return response.data;
        } catch (error) {
            throw new Error('Erro ao carregar o áudio');
        }
    }

    async awonGetEmbeddings(subdomain) {
        try {
            const response = await api.get('/api/awon/embeddings', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar vozes AWON');
        }
    }

    async awonGetCatalog(subdomain) {
        try {
            const response = await api.get('/api/awon/catalog', { params: { subdomain } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao listar catálogo AWON');
        }
    }

    async awonCreateKeyPhrase(payload) {
        try {
            const response = await api.post('/api/awon/key-phrases', payload);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao criar frase AWON');
        }
    }

    async awonUpdateKeyPhrase(payload) {
        try {
            const response = await api.put('/api/awon/key-phrases', payload);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao atualizar frase AWON');
        }
    }

    async awonGetPhraseQuality(subdomain, days) {
        try {
            const response = await api.get('/api/awon/phrase-quality', { params: { subdomain, days } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao consultar qualidade das frases AWON');
        }
    }

    async awonGetBehavior(subdomain, periodo) {
        try {
            const response = await api.get('/api/awon/behavior', { params: { subdomain, ...periodo } });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao consultar comportamentos AWON');
        }
    }

    async awonGetQueueStatus() {
        try {
            const response = await api.get('/api/awon/queue');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erro ao consultar fila AWON');
        }
    }

    /**
     * Publicador de Dashboards: o índice do Dash Analítico desta empresa.
     *
     * A empresa NÃO vai como parâmetro — o servidor a resolve pelo Host. Mandá-la
     * daqui seria pedir para o browser escolher de quem são os dados.
     */
    async listDashboards() {
        const response = await api.get('/api/dashboards');
        if (!Array.isArray(response.data)) {
            // routes/api.js não é passthrough: rota não declarada cai no catch-all e
            // volta 200 com o index.html. Sem esta checagem isso viraria um
            // `undefined.map()` na tela, três camadas longe da causa.
            throw new Error('Resposta inesperada ao listar dashboards');
        }
        return response.data;
    }

    /**
     * Remove um dashboard (remoção suave). Só o time da Aliado alcança: a rota
     * recusa quem não for, mesmo que a lixeira apareça na tela por engano.
     */
    async removeDashboard(ref) {
        try {
            const response = await api.delete(`/api/dashboards/${encodeURIComponent(ref)}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Não foi possível remover o dashboard');
        }
    }

    /**
     * Garante a sessão de COOKIE antes de abrir o iframe do dashboard publicado.
     *
     * O iframe e os `fetch('./data/<alias>')` do HTML não passam por este axios, então
     * não levam o X-Aliado-Auth — só o cookie. Depois de um deploy o servidor perdeu as
     * sessões (MemoryStore), e sem isto o iframe mostrava "Usuário não autenticado".
     * Um 401 aqui significa token inválido de verdade: o interceptor manda para o /login.
     */
    async restaurarSessaoCookie() {
        await api.post('/auth/sessao/restaurar');
    }

    /**
     * Sessão expirada vista fora do axios (o iframe do dashboard): mesmo destino do
     * interceptor — /login, com volta para a tela atual.
     */
    mandarParaLogin() {
        mandarParaLogin();
    }

    /**
     * O token assinado, verbatim, para o iframe do dashboard repassar no X-Aliado-Auth.
     * Nunca remontar: ver o comentário de LS_AUTH_TOKEN.
     */
    getAuthToken() {
        return localStorage.getItem(LS_AUTH_TOKEN);
    }

    /**
     * A URL do HTML do dashboard, para o `src` do iframe.
     *
     * Tem de ser exatamente esta rota: o HTML busca dados com `fetch('./data/<alias>')`,
     * e o `./` só resolve para /api/dashboards/<slug>/data/<alias> se o documento tiver
     * vindo de /api/dashboards/<slug>/html. Servir o mesmo HTML de outro caminho quebra
     * todos os fetches dele em silêncio.
     */
    dashboardHtmlUrl(slug, como) {
        const base = `/api/dashboards/${encodeURIComponent(slug)}/html`;
        // `?como=` só faz efeito sob preview — o servidor confere de novo. Aqui é só
        // repasse: quem revisa a permissão abre /dash/<ref>?como=<cargo|pessoa>.
        return como ? `${base}?como=${encodeURIComponent(como)}` : base;
    }

    /**
     * A empresa tem índice próprio, desenhado pela Aliado, no lugar da grade padrão?
     *
     * Nunca lança: qualquer falha vira `{existe:false}` e a tela monta a grade. O índice
     * é a única porta do cliente para os dashboards, então o caminho de erro tem de
     * levar à grade, não a um aviso.
     */
    async getIndiceCustomizado() {
        try {
            const response = await api.get('/api/dashboards/indice');
            return response.data?.existe === true ? response.data : { existe: false };
        } catch (e) {
            return { existe: false };
        }
    }

    /**
     * Busca o HTML do dashboard estático (POST /staticdash) via backend.
     * Retorna o HTML cru como string.
     */
    async getStaticDash(subdomain) {
        try {
            const response = await api.post('/api/staticdash', { subdomain }, {
                responseType: 'text',
                transformResponse: [(data) => data],
                timeout: 60000
            });
            return typeof response.data === 'string' ? response.data : String(response.data ?? '');
        } catch (error) {
            const data = error.response?.data;
            let msg = 'Erro ao carregar dashboard analítico';
            if (data) {
                try {
                    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                    msg = parsed?.error || msg;
                } catch (_) { /* ignore */ }
            }
            throw new Error(msg);
        }
    }

    // ===== SDUI WHITELABEL (admin) =====
    async sduiListClients(userId) {
        const r = await api.get('/api/sdui/clients', { params: { user_id: userId } });
        return r.data;
    }

    async sduiGetDefault(userId) {
        const r = await api.get('/api/sdui/default', { params: { user_id: userId } });
        return r.data;
    }

    async sduiSaveDefault({ userId, name, config }) {
        const r = await api.put('/api/sdui/default', { user_id: userId, name, config });
        return r.data;
    }

    async sduiListConfigs(userId, subdomain) {
        const r = await api.get('/api/sdui/configs', { params: { user_id: userId, subdomain } });
        return r.data;
    }

    // ----- Whitelabel por FASE: terceiro nível da cascata SDUI -----
    async sduiListPhaseConfigs(userId, subdomain, phaseId) {
        const r = await api.get('/api/sdui/phase-configs', { params: { user_id: userId, subdomain, phase_id: phaseId } });
        return r.data;
    }

    /** Devolve os TRÊS níveis (default, empresa, fase) e o merge — o config da fase é PARCIAL. */
    async sduiPhasePreview(userId, subdomain, phaseId, configId) {
        const r = await api.get('/api/sdui/phase-config/preview', { params: { user_id: userId, subdomain, phase_id: phaseId, config_id: configId } });
        return r.data;
    }

    async sduiSavePhaseConfig({ userId, subdomain, phaseId, id, name, config, activate }) {
        const r = await api.post('/api/sdui/phase-configs', { user_id: userId, subdomain, phase_id: phaseId, id, name, config, activate });
        return r.data;
    }

    async sduiDeletePhaseConfig({ userId, subdomain, id }) {
        const r = await api.delete('/api/sdui/phase-configs', { data: { user_id: userId, subdomain, id } });
        return r.data;
    }

    async sduiActivatePhaseConfig({ userId, subdomain, id }) {
        const r = await api.post('/api/sdui/phase-configs/activate', { user_id: userId, subdomain, id });
        return r.data;
    }

    async sduiPreview(userId, subdomain, configId) {
        const params = { user_id: userId, subdomain };
        if (configId != null) params.config_id = configId;
        const r = await api.get('/api/sdui/config/preview', { params });
        return r.data;
    }

    async sduiCreateConfig({ userId, subdomain, name, config, activate }) {
        const r = await api.post('/api/sdui/configs', { user_id: userId, subdomain, name, config, activate: !!activate });
        return r.data;
    }

    async sduiUpdateConfig({ userId, subdomain, id, name, config }) {
        const r = await api.put('/api/sdui/configs', { user_id: userId, subdomain, id, name, config });
        return r.data;
    }

    async sduiActivateConfig({ userId, subdomain, id }) {
        const r = await api.post('/api/sdui/configs/activate', { user_id: userId, subdomain, id });
        return r.data;
    }

    async sduiDeleteConfig({ userId, subdomain, id }) {
        const r = await api.delete('/api/sdui/configs', { data: { user_id: userId, subdomain, id } });
        return r.data;
    }

    // ----- Cenas SDUI (o user_id vem da sessão no servidor) -----
    async scenesList(subdomain) {
        const r = await api.get('/api/sdui/scenes', { params: { subdomain } });
        return r.data;
    }

    async scenesBody(subdomain, hash) {
        const r = await api.get('/api/sdui/scenes/body', { params: { subdomain, hash } });
        return r.data;
    }

    async scenesPut({ subdomain, name, body, dryRun }) {
        const r = await api.put('/api/sdui/scenes', { subdomain, name, body, dry_run: !!dryRun });
        return r.data;
    }

    async scenesDelete({ subdomain, hash }) {
        const r = await api.delete('/api/sdui/scenes', { data: { subdomain, hash } });
        return r.data;
    }

    async scenesStudioPass() {
        const r = await api.post('/api/sdui/scenes/studio-pass');
        return r.data;
    }

    async scenesRestore({ subdomain, hash }) {
        const r = await api.post('/api/sdui/scenes/restore', { subdomain, hash });
        return r.data;
    }

    async sduiUploadLogo({ userId, subdomain, file, scope }) {
        const params = new URLSearchParams({ user_id: String(userId), subdomain, filename: file.name || 'logo.png' });
        if (scope) params.append('scope', scope);
        const r = await api.post(`/api/sdui/logo?${params.toString()}`, file, {
            headers: { 'Content-Type': 'application/octet-stream' },
            timeout: 120000,
        });
        return r.data;
    }

    // ===== REVISÃO DE PONTOS (admin) =====

    async pointsResults({ subdomain, store_id, salesman_id, date_start, date_end }) {
        // jwt_token é injetado pelo proxy a partir da sessão; admin envia a empresa-alvo.
        const r = await api.post('/api/points-review/results', {
            subdomain, store_id, salesman_id, date_start, date_end,
        });
        return r.data;
    }

    async pointsGetConfig({ userId, subdomain, store_id }) {
        const r = await api.post('/api/points-review/config/get', { user_id_admin: userId, subdomain, store_id });
        return r.data;
    }

    // Régua de classificação (§N27): quais classificações caem em válido/não-válido. Só leitura.
    async pointsClassificationRules({ userId, subdomain }) {
        const r = await api.post('/api/points-review/classification-rules/get', { user_id_admin: userId, subdomain });
        return r.data;
    }

    // Grava uma NOVA VIGÊNCIA da régua (vale a partir de amanhã). Nunca altera a que já vale.
    async pointsSaveClassificationRules({ userId, subdomain, items }) {
        const r = await api.post('/api/points-review/classification-rules/save', { user_id_admin: userId, subdomain, items });
        return r.data;
    }

    // `valid_from` (AAAA-MM-DD): a partir de quando o preço vale; omitido = amanhã (hoje e o passado a API recusa).
    // O que já aconteceu continua pelo preço da época, e dá para trocar no meio de uma meta.
    async pointsSaveConfig({ userId, subdomain, store_id, items, valid_from }) {
        const r = await api.put('/api/points-review/config', {
            user_id_admin: userId, subdomain, store_id, items, valid_from,
        });
        return r.data;
    }

    // Remover o override reprecifica o passado da loja: a API recusa (409) sem `confirm_retroactive`.
    async pointsResetConfig({ userId, subdomain, store_id, confirm_retroactive }) {
        const r = await api.post('/api/points-review/config/reset', {
            user_id_admin: userId, subdomain, store_id, confirm_retroactive,
        });
        return r.data;
    }

    async pointsGetBehaviors({ userId, subdomain, store_id }) {
        const r = await api.post('/api/points-review/behaviors/get', { user_id_admin: userId, subdomain, store_id });
        return r.data;
    }

    async pointsSaveBehaviors({ userId, subdomain, store_id, items }) {
        const r = await api.put('/api/points-review/behaviors', { user_id_admin: userId, subdomain, store_id, items });
        return r.data;
    }

    async pointsDeleteBehavior({ userId, subdomain, store_id, behavior_key }) {
        const r = await api.post('/api/points-review/behaviors/delete', { user_id_admin: userId, subdomain, store_id, behavior_key });
        return r.data;
    }

    async pointsGetObjectionCategories({ userId, subdomain, store_id }) {
        const r = await api.post('/api/points-review/objection-categories/get', { user_id_admin: userId, subdomain, store_id });
        return r.data;
    }

    async pointsSaveObjectionCategories({ userId, subdomain, store_id, items }) {
        const r = await api.put('/api/points-review/objection-categories', { user_id_admin: userId, subdomain, store_id, items });
        return r.data;
    }

    async pointsDeleteObjectionCategory({ userId, subdomain, store_id, slug }) {
        const r = await api.post('/api/points-review/objection-categories/delete', { user_id_admin: userId, subdomain, store_id, slug });
        return r.data;
    }

    async pointsGetLostSalesReasonCategories({ userId, subdomain, store_id }) {
        const r = await api.post('/api/points-review/lost-sales-reason-categories/get', { user_id_admin: userId, subdomain, store_id });
        return r.data;
    }

    async pointsSaveLostSalesReasonCategories({ userId, subdomain, store_id, items }) {
        const r = await api.put('/api/points-review/lost-sales-reason-categories', { user_id_admin: userId, subdomain, store_id, items });
        return r.data;
    }

    async pointsDeleteLostSalesReasonCategory({ userId, subdomain, store_id, slug }) {
        const r = await api.post('/api/points-review/lost-sales-reason-categories/delete', { user_id_admin: userId, subdomain, store_id, slug });
        return r.data;
    }

    async pointsRecordingStatus({ recorder_id, subdomain }) {
        // jwt_token é injetado pelo proxy a partir da sessão.
        const r = await api.post('/api/points-review/recording-status', { recorder_id, subdomain });
        return r.data;
    }

    async pointsRanking({ subdomain, date_start, date_end }) {
        // jwt_token é injetado pelo proxy a partir da sessão.
        const r = await api.post('/api/points-review/ranking', { subdomain, date_start, date_end });
        return r.data;
    }

    async pointsGoals({ subdomain }) {
        // jwt_token é injetado pelo proxy a partir da sessão; admin envia a empresa-alvo.
        const r = await api.post('/api/points-review/goals', { subdomain });
        return r.data;
    }

    // CRUD admin de metas (tela Configuração de Metas). user_id_admin é injetado pelo proxy a
    // partir da sessão (não confia no client) — não precisa ser enviado aqui.
    async pointsGoalsList({ subdomain, store_id }) {
        const r = await api.post('/api/points-review/goals/list', { subdomain, store_id });
        return r.data;
    }

    async pointsGoalsSave(goal) {
        const r = await api.post('/api/points-review/goals/save', goal);
        return r.data;
    }

    async pointsGoalsDelete({ subdomain, id }) {
        const r = await api.post('/api/points-review/goals/delete', { subdomain, id });
        return r.data;
    }

    // CRUD admin de tetos de pontos (aba Tetos). user_id_admin é injetado pelo proxy a partir da sessão.
    async pointsCapsList({ subdomain, store_id }) {
        const r = await api.post('/api/points-review/caps/list', { subdomain, store_id });
        return r.data;
    }

    async pointsCapsSave(cap) {
        const r = await api.post('/api/points-review/caps/save', cap);
        return r.data;
    }

    async pointsCapsDelete({ subdomain, id }) {
        const r = await api.post('/api/points-review/caps/delete', { subdomain, id });
        return r.data;
    }

    // Escalas de trabalho (menu Escalas). user_id_admin é injetado pelo proxy a partir da sessão.
    // Todas passam o corpo cru para /schedule/* da API (docs/escalas.md no aliado-recorder-api).
    async schedulePost(path, body) {
        const r = await api.post(`/api/schedule/${path}`, body || {});
        return r.data;
    }

    scheduleListPatterns({ subdomain }) { return this.schedulePost('patterns/list', { subdomain }); }
    scheduleSavePattern(p) { return this.schedulePost('patterns/save', p); }
    scheduleDeletePattern({ subdomain, id }) { return this.schedulePost('patterns/delete', { subdomain, id }); }
    scheduleListAssignments({ subdomain, ...f }) { return this.schedulePost('assignments/list', { subdomain, ...f }); }
    scheduleSaveAssignment(a) { return this.schedulePost('assignments/save', a); }
    scheduleDeleteAssignment({ subdomain, id }) { return this.schedulePost('assignments/delete', { subdomain, id }); }
    scheduleListExceptions({ subdomain, ...f }) { return this.schedulePost('exceptions/list', { subdomain, ...f }); }
    scheduleSaveException(e) { return this.schedulePost('exceptions/save', e); }
    // `reason`: obrigatório quando a exceção toca dia fechado (a API recusa sem ele); em dia aberto pode ir vazio.
    scheduleDeleteException({ subdomain, id, reason }) {
        return this.schedulePost('exceptions/delete', { subdomain, id, ...(reason ? { reason } : {}) });
    }
    scheduleListNational(f) { return this.schedulePost('national/list', f); }
    scheduleSaveNational(n) { return this.schedulePost('national/save', n); }
    scheduleDeleteNational({ id }) { return this.schedulePost('national/delete', { id }); }
    scheduleSeedNational({ year, dry_run }) { return this.schedulePost('national/seed', { year, dry_run }); }
    scheduleListStoreSettings({ subdomain }) { return this.schedulePost('store-settings/list', { subdomain }); }
    scheduleSaveStoreSettings(s) { return this.schedulePost('store-settings/save', s); }
    scheduleResolve(r) { return this.schedulePost('resolve', r); }
    scheduleFreezeStatus({ subdomain }) { return this.schedulePost('freeze/status', { subdomain }); }

    // CRUD admin de keyterms (tela Palavras-chave). user_id_admin é injetado pelo proxy a partir
    // da sessão (não confia no client). store_id ausente/null = padrão da empresa.
    async keytermsList({ subdomain, store_id }) {
        const r = await api.post('/api/keyterms/list', { subdomain, store_id });
        return r.data;
    }

    async keytermsSave({ id, subdomain, store_id, term }) {
        const r = await api.post('/api/keyterms/save', { id, subdomain, store_id, term });
        return r.data;
    }

    async keytermsDelete({ subdomain, id }) {
        const r = await api.post('/api/keyterms/delete', { subdomain, id });
        return r.data;
    }

    // Pushtokens (tela Versões do app) — somente leitura. user_id_admin é injetado pelo proxy a
    // partir da sessão (não confia no client).
    // Tabela geral: aparelhos por cliente x versão do app, contando 1 por vendedor.
    async pushtokensOverview({ include_internal = false } = {}) {
        const r = await api.post('/api/pushtokens/overview', { include_internal });
        return r.data;
    }

    // Detalhamento de um cliente: 1 linha por vendedor (registro mais recente) + histórico.
    // app_version === '__null__' filtra os aparelhos sem versão registrada.
    async pushtokensList({ subdomain, store_id, salesman_id, app_version, include_deleted = false, show_all_rows = false }) {
        const r = await api.post('/api/pushtokens/list', {
            subdomain, store_id, salesman_id, app_version, include_deleted, show_all_rows,
        });
        return r.data;
    }

    // Regras e Conquistas (tela Regras): o motor de regras da recorder-api. user_id_admin é injetado
    // pelo proxy a partir da sessão — não enviar daqui.
    async rulesCall(path, body = {}) {
        const r = await api.post(`/api${path}`, body);
        return r.data;
    }

    // Termo de aceite do app (tela Termos). user_id_admin é injetado pelo proxy a partir da sessão.
    async termsVersionsList({ code } = {}) {
        const r = await api.post('/api/terms/admin/versions/list', code ? { code } : {});
        return r.data;
    }

    async termsVersionSave({ id, company_id, store_id, title, body_md }) {
        const r = await api.post('/api/terms/admin/versions/save', { id, company_id, store_id, title, body_md });
        return r.data;
    }

    async termsVersionPublish({ id }) {
        const r = await api.post('/api/terms/admin/versions/publish', { id });
        return r.data;
    }

    async termsVersionDelete({ id }) {
        const r = await api.post('/api/terms/admin/versions/delete', { id });
        return r.data;
    }

    async termsPoliciesList() {
        const r = await api.post('/api/terms/admin/policies/list', {});
        return r.data;
    }

    // enforcement: 'off' | 'required' | 'explicit_refusal' | null/'inherit' (remove a do escopo)
    async termsPolicySave({ company_id, store_id, enforcement }) {
        const r = await api.post('/api/terms/admin/policies/save', { company_id, store_id, enforcement });
        return r.data;
    }

    async termsReport({ company_id, store_id }) {
        const r = await api.post('/api/terms/admin/report', { company_id, store_id });
        return r.data;
    }

    // CRUD admin dos comunicados do app (tela Comunicados): banner, dica e frase de comportamento.
    // user_id_admin é injetado pelo proxy a partir da sessão e é quem fica gravado como autor do
    // texto — não enviar daqui.
    async communicationsList({ subdomain, store_id, types, include_inactive = true, include_expired = true }) {
        const r = await api.post('/api/communications/list', { subdomain, store_id, types, include_inactive, include_expired });
        return r.data;
    }

    async communicationsSave(communication) {
        const r = await api.post('/api/communications/save', communication);
        return r.data;
    }

    async communicationsDelete({ subdomain, id }) {
        const r = await api.post('/api/communications/delete', { subdomain, id });
        return r.data;
    }

    // Liga/desliga a exibição sem passar pelo formulário (não toca em texto, alvo, datas nem versão).
    async communicationsToggleActive({ subdomain, id, active }) {
        const r = await api.post('/api/communications/toggle-active', { subdomain, id, active });
        return r.data;
    }

    // Régua de comunicação: barras + segmentos efetivos por tipo (mesma resolução do endpoint do
    // app). Cobre os 3 tipos; frase de comportamento entra como barra sem início/fim.
    async communicationsTimeline({ subdomain, date_start, date_end, store_id, salesman_id, types, include_cohorts }) {
        const r = await api.post('/api/communications/timeline', { subdomain, date_start, date_end, store_id, salesman_id, types, include_cohorts });
        return r.data;
    }

    // Quem leu, quem confirmou e quem reagiu (like/dislike), com as datas.
    async communicationsInteractions({ subdomain, id }) {
        const r = await api.post('/api/communications/interactions', { subdomain, id });
        return r.data;
    }

    // §N31 — push. `rules` é o registro de regras (o formulário é montado a partir dele); `preview`
    // diz quem receberia agora, com o motivo; `deliveries` é o histórico de horários e envios.
    // As três falham alto se a resposta vier fora de forma: rota não registrada no Express devolve
    // 200 com o index.html, e a tela quebraria num `.map` de undefined.
    async communicationsPushRules() {
        const r = await api.post('/api/communications/push/rules', {});
        if (!Array.isArray(r.data?.rules)) throw new Error('Resposta inesperada de /push/rules');
        return r.data;
    }

    async communicationsPushPreview({ subdomain, id, slot_at }) {
        const r = await api.post('/api/communications/push/preview', { subdomain, id, slot_at });
        if (!Array.isArray(r.data?.recipients)) throw new Error('Resposta inesperada de /push/preview');
        return r.data;
    }

    async communicationsPushDeliveries({ subdomain, id, limit = 20 }) {
        const r = await api.post('/api/communications/push/deliveries', { subdomain, id, limit });
        if (!Array.isArray(r.data?.slots)) throw new Error('Resposta inesperada de /push/deliveries');
        return r.data;
    }

    async pointsSalesmen({ userId, subdomain, store_id }) {
        const r = await api.post('/api/points-review/salesmen', {
            user_id_admin: userId, subdomain, store_id,
        });
        return r.data;
    }

    // Atendimento virtual (proxy injeta user_id_admin da sessão).
    async pointsClassifications({ subdomain }) {
        const r = await api.post('/api/points-review/classifications', { subdomain });
        return r.data;
    }

    async pointsCreateVirtualAttendance({ subdomain, salesman_id, classification_id, date_start, date_end }) {
        const r = await api.post('/api/points-review/virtual-attendance', {
            subdomain, salesman_id, classification_id, date_start, date_end,
        });
        return r.data;
    }

    async pointsAttendances({ userId, subdomain, store_id, salesman_id, date_start, date_end }) {
        const r = await api.post('/api/points-review/attendances', {
            user_id_admin: userId, subdomain, store_id, salesman_id, date_start, date_end,
        });
        return r.data;
    }

    async pointsAttendance({ userId, subdomain, transcription_id }) {
        const r = await api.post('/api/points-review/attendance', {
            user_id_admin: userId, subdomain, transcription_id,
        });
        return r.data;
    }

    async pointsSaveReview({ userId, subdomain, transcription_id, items }) {
        const r = await api.post('/api/points-review/save', {
            user_id_admin: userId, subdomain, transcription_id, items,
        });
        return r.data;
    }

    async pointsTranscription({ userId, subdomain, transcription_id }) {
        const r = await api.post('/api/points-review/transcription', {
            user_id_admin: userId, subdomain, transcription_id,
        });
        return r.data;
    }

    // AWON: transcrição original × resultante do julgador (falas marcadas ficou / saiu / entrou), só para ver
    async pointsAwonComparacao({ userId, subdomain, transcription_id }) {
        const r = await api.post('/api/points-review/awon-comparacao', {
            user_id_admin: userId, subdomain, transcription_id,
        });
        return r.data;
    }

    async pointsAudio({ userId, subdomain, transcription_id, redacted = false }) {
        const r = await api.post('/api/points-review/audio', {
            user_id_admin: userId, subdomain, transcription_id, redacted,
        });
        return r.data;
    }

    async pointsAttendanceData({ userId, subdomain, transcription_id }) {
        const r = await api.post('/api/points-review/attendance-data', {
            user_id_admin: userId, subdomain, transcription_id,
        });
        return r.data;
    }

    async pointsMarkReview({ userId, subdomain, transcription_id, reviewed = true }) {
        const r = await api.post('/api/points-review/mark-review', {
            user_id_admin: userId, subdomain, transcription_id, reviewed,
        });
        return r.data;
    }

    // score-disputes: painel e lista de reclamações (par das rotas em node/routes/api.js)
    async pointsDisputesSummary({ userId, subdomain, store_id, salesman_id, date_start, date_end, include_before_activation }) {
        const r = await api.post('/api/points-review/disputes/summary', {
            user_id_admin: userId, subdomain, store_id, salesman_id, date_start, date_end, include_before_activation,
        });
        return r.data;
    }

    // kind: 'dispute' (VAR) | 'dislike' (👎 no feedback sem VAR) | undefined (os dois);
    // feedback_ids: deep link do alerta de dislike da Home
    async pointsDisputesList({ userId, subdomain, store_id, salesman_id, date_start, date_end, include_before_activation, status, order, kind, feedback_ids }) {
        const r = await api.post('/api/points-review/disputes/list', {
            user_id_admin: userId, subdomain, store_id, salesman_id, date_start, date_end, include_before_activation, status, order, kind, feedback_ids,
        });
        return r.data;
    }

    // Fila de reclamações por empresa (contagem de pendentes), caso de uma reclamação e envio único
    async pointsDisputesOverview({ userId, limit = 20, date_start, date_end }) {
        const r = await api.post('/api/points-review/disputes/overview', { user_id_admin: userId, limit, date_start, date_end });
        return r.data;
    }

    async pointsDisputeCase({ userId, subdomain, transcription_id }) {
        const r = await api.post('/api/points-review/disputes/case', { user_id_admin: userId, subdomain, transcription_id });
        return r.data;
    }

    async pointsDisputeSubmit({ userId, subdomain, transcription_id, dispute_id, items, comments_answer }) {
        const r = await api.post('/api/points-review/disputes/submit', {
            user_id_admin: userId, subdomain, transcription_id, dispute_id, items, comments_answer,
        });
        return r.data;
    }

    // Promoção de whitelabel (par das rotas em node/routes/api.js). Ler leva o user_id da sessão,
    // posto pelo BFF; decidir vai com o login de quem decide, também resolvido no BFF.
    async whitelabelPromotionsList({ subdomain, status, limite = 100 } = {}) {
        const r = await api.post('/api/whitelabel-promotions/list', { subdomain, status, limite });
        return r.data;
    }

    async whitelabelPromotionGet(promotionId) {
        const r = await api.post('/api/whitelabel-promotions/get', { promotion_id: Number(promotionId), com_conteudo: true });
        return r.data;
    }

    async whitelabelPromotionCanDecide() {
        const r = await api.post('/api/whitelabel-promotions/can-decide', {});
        return r.data;
    }

    async whitelabelPromotionDecide(acao, promotionId, nota) {
        const r = await api.post(`/api/whitelabel-promotions/${acao}`, { promotion_id: Number(promotionId), nota: nota || null });
        return r.data;
    }

    async whitelabelPromotionBatchGet(batchId) {
        const r = await api.post('/api/whitelabel-promotions/batch/get', { batch_id: batchId });
        return r.data;
    }

    async whitelabelPromotionBatchDecide(acao, batchId, promotionIds, nota) {
        const r = await api.post(`/api/whitelabel-promotions/batch/${acao}`, {
            batch_id: batchId, promotion_ids: promotionIds, nota: nota || null,
        });
        return r.data;
    }

    async pointsFeedbackAnswer({ userId, subdomain, feedback_id, comments_answer }) {
        const r = await api.post('/api/points-review/feedback-answer', {
            user_id_admin: userId, subdomain, feedback_id, comments_answer,
        });
        return r.data;
    }

    // ===== DASH BUILDER — Endpoints Publicados =====
    // ===== MCP: auditoria e permissões =====
    // O par do `routes/api.js` — naquele repo as rotas são declaradas uma a uma, e endpoint
    // novo NÃO aparece de graça: sem o par, a chamada cai no catch-all e o browser recebe
    // 200 com um corpo de outra forma, o que estoura em `undefined.map()` na tela.
    async getMcpAudit(params = {}) {
        const r = await api.get('/api/mcp/audit', { params });
        return r.data;
    }

    async getMcpAuditResumo(params = {}) {
        const r = await api.get('/api/mcp/audit/resumo', { params });
        return r.data;
    }

    async getMcpUso(params = {}) {
        const r = await api.get('/api/mcp/uso', { params });
        return r.data;
    }

    async getMcpLacunas(params = {}) {
        const r = await api.get('/api/mcp/lacunas', { params });
        return r.data;
    }

    async getMcpPermissions() {
        const r = await api.get('/api/mcp/permissions');
        return r.data;
    }

    // Concede/revoga permissão num dos MCPs. O corpo é o DELTA montado pela tela
    // ({ env, email, conceder, revogar, motivo }) — quem ela é (o ATOR) NÃO vai daqui:
    // o Express põe em header a partir da sessão, porque corpo vindo do browser é
    // escolhido por quem clica. Ver node/routes/api.js e services/apiService.js.
    async setMcpPermissions(corpo) {
        const r = await api.post('/api/mcp/permissions', corpo);
        return r.data;
    }

    async getEndpoints() {
        const r = await api.get('/api/endpoints');
        return r.data;
    }

    async getEndpoint(id) {
        const r = await api.get(`/api/endpoints/${id}`);
        return r.data;
    }

    async generateEndpointToken(id, { expires_in_minutes, label } = {}) {
        const r = await api.post(`/api/endpoints/${id}/tokens`, { expires_in_minutes, label });
        return r.data;
    }

    async revokeEndpointToken(id, jti) {
        const r = await api.delete(`/api/endpoints/${id}/tokens/${jti}`);
        return r.data;
    }

    async deleteEndpoint(id) {
        const r = await api.delete(`/api/endpoints/${id}`);
        return r.data;
    }

    // ===================== FASES DE ATENDIMENTO =====================
    // Plano: aliado-recorder-api/docs/fases-atendimento-plano.md (§9).
    // `user_id_admin` é injetado pelo PROXY a partir da sessão — nunca enviado pelo client,
    // senão o front poderia se declarar admin. Mesmo padrão do CRUD de metas.

    // --- programas e fases (configuração) ---
    async phaseProgramsList({ subdomain }) {
        const r = await api.post('/api/phases/programs/list', { subdomain });
        return r.data;
    }

    async phaseProgramSave({ subdomain, id, name }) {
        const r = await api.post('/api/phases/programs/save', { subdomain, id, name });
        return r.data;
    }

    /** Cria ou edita fase. Campos estruturais (duration_days, sort_order, phase_type_key,
     *  advance_mode) ficam READ-ONLY na API depois que alguém entrou na fase (Gap 11) — a tela
     *  deve refletir isso, e a API recusa com 409 se tentar. */
    // `phaseResolve` e `phaseMemberAdvance` já existem mais abaixo — não duplicar: em classe JS a
    // definição de baixo vence, e a de cima vira código morto que parece estar em uso.
    /** Reaplica o plano do programa à turma. `dry_run` (default true) devolve o PREVIEW. */
    async phaseReapplyPlan({ subdomain, cohort_id, dry_run = true }) {
        const r = await api.post('/api/phases/cohorts/reapply-plan', { subdomain, cohort_id, dry_run });
        return r.data;
    }

    /** Planejado da turma := o que os membros vivem. `dry_run` (default true) devolve antes/depois. */
    async phaseAlignCohortPlan({ subdomain, cohort_id, dry_run = true }) {
        const r = await api.post('/api/phases/cohorts/align-plan', { subdomain, cohort_id, dry_run });
        return r.data;
    }

    /** SÓ EM QA: desfaz o avanço forçado devolvendo a trilha do membro ao plano (§9.14). */
    async phaseRebuildTrail({ subdomain, member_id, reseal = true }) {
        const r = await api.post('/api/phases/members/rebuild-trail', { subdomain, member_id, reseal });
        return r.data;
    }

    /** Redimensiona fase JÁ PERCORRIDA (mesma rota do `resize_phase` do MCP). dry_run default true. */
    async phaseResize({ subdomain, phase_id, duration_days, dry_run = true }) {
        const r = await api.post('/api/phases/phases/resize', { subdomain, phase_id, duration_days, dry_run, reseal: true });
        return r.data;
    }

    async phaseSave(payload) {
        const r = await api.post('/api/phases/phases/save', payload);
        return r.data;
    }

    /** §N26 — quem já destravou feedback/histórico nesta turma, e quanto falta para os outros. */
    async phaseUnlockList({ subdomain, cohort_id }) {
        const r = await api.post('/api/phases/unlock/list', { subdomain, cohort_id });
        return r.data;
    }

    /** §N26 — reavalia o destrave. `dry_run` só reporta; sem ele, grava a data de quem cruzou.
     *  Idempotente: quem já destravou volta como `already` e não é reescrito. */
    async phaseUnlockEvaluate({ subdomain, cohort_id, salesman_id, store_id, dry_run = false }) {
        const r = await api.post('/api/phases/unlock/evaluate',
            { subdomain, cohort_id, salesman_id, store_id, dry_run });
        return r.data;
    }

    // --- agrupamento de turmas (árvore de grupos) ---
    async cohortGroupList({ subdomain, include_cohorts = true, tag }) {
        const r = await api.post('/api/cohort-groups/list', { subdomain, include_cohorts, tag });
        return r.data;
    }

    async cohortGroupSave(payload) {
        const r = await api.post('/api/cohort-groups/save', payload);
        return r.data;
    }

    async cohortGroupDelete({ subdomain, id, effective_on }) {
        const r = await api.post('/api/cohort-groups/delete', { subdomain, id, effective_on });
        return r.data;
    }

    async cohortGroupMemberAdd({ subdomain, group_id, cohort_id, effective_on }) {
        const r = await api.post('/api/cohort-groups/members/add', { subdomain, group_id, cohort_id, effective_on });
        return r.data;
    }

    async cohortGroupMemberRemove({ subdomain, group_id, cohort_id, effective_on }) {
        const r = await api.post('/api/cohort-groups/members/remove', { subdomain, group_id, cohort_id, effective_on });
        return r.data;
    }

    async cohortGroupMovements({ subdomain, group_id, cohort_id, limit = 200 }) {
        const r = await api.post('/api/cohort-groups/movements/list', { subdomain, group_id, cohort_id, limit });
        return r.data;
    }

    // --- turmas e membros (operação) ---
    async phaseCohortsList({ subdomain, program_id }) {
        const r = await api.post('/api/phases/cohorts/list', { subdomain, program_id });
        return r.data;
    }

    async phaseCohortDetail({ subdomain, cohort_id }) {
        const r = await api.post('/api/phases/cohorts/detail', { subdomain, cohort_id });
        return r.data;
    }

    async phaseCohortSave(payload) {
        const r = await api.post('/api/phases/cohorts/save', payload);
        return r.data;
    }

    async phaseMemberAdd({ subdomain, cohort_id, salesman_id, date_start }) {
        const r = await api.post('/api/phases/members/add', { subdomain, cohort_id, salesman_id, date_start });
        return r.data;
    }

    /** Saída antecipada. `exit_on` é o PRIMEIRO DIA FORA: o vendedor ainda conta no dia anterior.
     *  É soft delete que preserva o histórico (§N1) — não apaga a passagem dele pela fase. */
    async phaseMemberRemove({ subdomain, member_id, exit_on }) {
        const r = await api.post('/api/phases/members/remove', { subdomain, member_id, exit_on });
        return r.data;
    }

    async phaseMemberAdvance(payload) {
        const r = await api.post('/api/phases/members/advance', payload);
        return r.data;
    }

    // --- leitura ---
    async phaseResolve({ subdomain, salesman_id, transcription_id, at_date }) {
        const r = await api.post('/api/phases/resolve', { subdomain, salesman_id, transcription_id, at_date });
        return r.data;
    }

    /** Fase vigente de MUITOS vendedores numa chamada. Use via useFasesDosVendedores. */
    async phaseResolveBatch({ subdomain, salesman_ids, store_id, at_date }) {
        const r = await api.post('/api/phases/resolve/batch', { subdomain, salesman_ids, store_id, at_date });
        return r.data;
    }

    /**
     * Fase SELADA de muitos atendimentos. Para telas que listam ATENDIMENTO, é este e não o
     * phaseResolveBatch: a fase de hoje pendurada num atendimento de março diria que ele foi
     * pontuado por uma régua que não valia naquele dia.
     */
    async phaseSealsBatch({ subdomain, transcription_ids }) {
        const r = await api.post('/api/phases/seals/batch', { subdomain, transcription_ids });
        return r.data;
    }

    /**
     * Régua de fases por LOJA da empresa (§9.10). Traz TODAS as lojas ativas — inclusive as sem
     * turma, que é a resposta para "quem ainda não tem programa".
     */
    async phaseStoresTimeline({ subdomain, date_start, date_end }) {
        const r = await api.post('/api/phases/timeline/stores', { subdomain, date_start, date_end });
        return r.data;
    }

    /** Ranking da TURMA (§9.11): pontos de cada membro, somados nas janelas de fase deles. */
    async cohortRanking({ subdomain, cohort_id, date_start, date_end }) {
        const r = await api.post('/api/points-review/ranking-by-cohort',
            { subdomain, cohort_id, date_start, date_end });
        return r.data;
    }

    async phaseTimeline({ subdomain, salesman_id }) {
        // POST desde 13/08: a API aceita as duas formas, mas o corpo é a canônica.
        const r = await api.post('/api/phases/timeline', { subdomain, salesman_id });
        return r.data;
    }

    // --- régua e metas por fase ---
    /** Devolve o configurado NA fase e o herdado (loja/global) juntos, para a tela distinguir
     *  "sobrescrito" de "apenas exibido". */
    async phaseRulerList({ subdomain, phase_id }) {
        const r = await api.post('/api/phases/ruler/list', { subdomain, phase_id });
        return r.data;
    }

    /** A API recusa (409) régua própria em fase que não trava o placar — §N21. */
    // `confirm_retroactive`: trocar o valor de uma régua de fase que já pontuou atendimento reprecifica o que já
    // foi contado (a régua da fase não tem vigência); a API recusa (409) sem este reconhecimento.
    async phasePointsConfigSave({ subdomain, phase_id, metric, points_per_unit, confirm_retroactive }) {
        const r = await api.post('/api/phases/points-config/save', { subdomain, phase_id, metric, points_per_unit, confirm_retroactive });
        return r.data;
    }

    /** Remove um comportamento DA FASE (por id). Nunca use o delete do ongoing no escopo de fase:
     *  aquele, com store_id nulo, apaga o comportamento global da empresa. */
    async phaseBehaviorDelete({ subdomain, id }) {
        const r = await api.post('/api/phases/behaviors/delete', { subdomain, id });
        return r.data;
    }

    /** Remove a régua de métrica DA FASE (por id). A fase volta a herdar a da loja/global. */
    async phasePointsConfigDelete({ subdomain, id, confirm_retroactive }) {
        const r = await api.post('/api/phases/points-config/delete', { subdomain, id, confirm_retroactive });
        return r.data;
    }

    async phaseBehaviorSave(payload) {
        const r = await api.post('/api/phases/behaviors/save', payload);
        return r.data;
    }

    async phaseGoalsList({ subdomain, phase_id, cohort_id }) {
        const r = await api.post('/api/phases/goals/list', { subdomain, phase_id, cohort_id });
        return r.data;
    }

    async phaseGoalSave(payload) {
        const r = await api.post('/api/phases/goals/save', payload);
        return r.data;
    }

    async phaseGoalDelete({ subdomain, id }) {
        const r = await api.post('/api/phases/goals/delete', { subdomain, id });
        return r.data;
    }

    /** Quais slugs a fase sobrescreve E quais ela tem direito de sobrescrever (a restrição
     *  tipo x slug), para a tela não oferecer o que a API vai recusar. */
    async phasePromptAssignments({ subdomain, phase_id }) {
        const r = await api.post('/api/phases/prompt-assignments/list', { subdomain, phase_id });
        return r.data;
    }

    // ─── Sincronizador de Ambientes (Fase 1: leitura) ──────────────────────────
    // O ambiente é sempre parâmetro explícito; o Express resolve com `resolveEnvTarget`,
    // que é fail-closed. Nada aqui infere ambiente do host.

    /** Ambientes utilizáveis (os que têm credencial configurada no servidor). */
    async syncEnvs() {
        const r = await api.get('/api/sync/envs');
        return r.data;
    }

    /** Diff por entidade entre dois ambientes. O corte é fixado no servidor e volta na resposta. */
    async syncDiff({ origem, destino, subdomain, corte, classe }) {
        const r = await api.post('/api/sync/diff', { origem, destino, subdomain, corte, classe });
        return r.data;
    }

    /** Itens de uma entidade, já classificados em ausente / divergente / igual. */
    async syncItems({ origem, destino, subdomain, entidade, corte }) {
        const r = await api.post('/api/sync/items', { origem, destino, subdomain, entidade, corte });
        return r.data;
    }

    /** Grava o plano de sincronização (sempre no banco de QA). */
    async syncCriarPlano(corpo) {
        const r = await api.post('/api/sync/plan', corpo);
        return r.data;
    }

    /** Um plano com seus itens agrupados por entidade. */
    async syncLerPlano(planId) {
        const r = await api.post('/api/sync/plan/get', { plan_id: planId });
        return r.data;
    }

    /** Planos recentes, para o histórico. */
    async syncListarPlanos({ subdomain, limite } = {}) {
        const r = await api.post('/api/sync/plan/list', { subdomain, limite });
        return r.data;
    }

    /** Simula (dry_run) ou aplica um plano. */
    async syncExecutar({ planId, dryRun = true }) {
        const r = await api.post('/api/sync/executar', { plan_id: planId, dry_run: dryRun });
        return r.data;
    }

    /** Histórico de execuções dos três ambientes. */
    async syncRuns({ planId } = {}) {
        const r = await api.post('/api/sync/runs', { plan_id: planId });
        return r.data;
    }

    /**
     * Desfaz um PLANO aplicado. A unidade é o plano e não a execução: um plano com duas
     * entidades gera dois runs, e desfazer só um deixa o outro inconsistente.
     */
    async syncDesfazer({ ambiente, planId, dryRun = true }) {
        const r = await api.post('/api/sync/rollback',
            { ambiente, plan_id: planId, dry_run: dryRun });
        return r.data;
    }
}

// Exportar instância única
const apiService = new ApiService();
export default apiService;